<template lang="html">
  <div class="cw-scan fill-mobile">
    <div class="card box">
      <div class="card-image">
        <div class="hero is-small is-black" :style="safeAreaInsetStyle">
          <div class="hero-body has-text-centered">
            <div class="centerit">
              <div
                class="box-loader"
                :class="{ 'is-warning': !nfcAvailable }"
                @click="fakeScanBox"
              >
                <div class="box-container">
                  <div class="box-number">
                    {{
                      (nfcAvailable && scannedBoxNumber) ||
                        (!nfcAvailable && 'X') ||
                        ''
                    }}
                  </div>
                  <div v-if="nfcAvailable" class="loader filled"></div>
                </div>
              </div>
            </div>
            <h1 class="title is-3">
              {{ currentOpperation }}
            </h1>
          </div>
        </div>
      </div>
      <div class="card-content">
        <div class="content">
          <p class="is-size-5">
            <span v-if="currentOpperation === 'Scanning'">
              Hold the device near the tag on the box. Wait until the tag has
              registered or you can find the box by its id</span
            >
            <span v-if="currentOpperation === 'Processing'">
              One moment...</span
            >
            <span v-if="currentOpperation === 'Find Manually'"
              >Your device cannot scan NFC tags. Please find the box
              manually.</span
            >
          </p>
        </div>
        <cw-service-data-cell
          description="Find Manually"
          inputValue=""
          :editing.sync="userChoseFindManually"
          expandedHeight="50vh"
          overflowType="scroll"
          :padInput="false"
        >
          <div slot="input-control">
            <input
              class="input is-underlined"
              v-model="boxFilter"
              type="text"
              placeholder="Search Project, Lot Number, or Box Number"
            />
            <div class="has-text-centered is-padded-small">
              <span v-if="loadingBoxes" class="icon has-text-primary is-large">
                <i class="las la-2x la-sync fa-spin"></i>
              </span>
            </div>
            <cw-box-list
              :boxes="filteredBoxes"
              :selectionHandler="boxSelected"
              :showProjectInfoInCells="true"
            />
          </div>
        </cw-service-data-cell>
      </div>
    </div>
  </div>
</template>

<script>
import authRequired from '@/components/mixins/auth-required';
import autoRefresh from '@/components/mixins/auto-refresh';
import screenshotConfig from '@/components/mixins/screenshot-config';

// FOR SCREENSHOTS
import Mock from '@/mock-data/screenshots';

import {
  DISPATCH_SETS_CURRENT,
  DISPATCH_SET_BY_BOX,
} from '@/graphql/constants';

export default {
  name: 'cw-scan',
  // share common functionality with component mixins
  mixins: [authRequired, autoRefresh, screenshotConfig],
  props: {
    visible: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  // variables
  data() {
    return {
      nfcAvailable: false,
      userChoseFindManually: false,
      boxes: [],
      loadingBoxes: 0,
      boxFilter: '',
      scannedBoxNumber: '',
      scannedBoxInfo: null,
    };
  },
  computed: {
    findManually() {
      return this.userChoseFindManually || !this.nfcAvailable;
    },
    filteredBoxes() {
      let boxes = this.boxes;

      let query = this.boxFilter.toLowerCase().replace(/lot /g, '');
      query = query.trim();
      if (query.length > 0) {
        boxes = boxes.filter(boxInfo => {
          let matchBoxNumber =
            boxInfo.box &&
            boxInfo.box.boxNumber.toLowerCase().indexOf(query) > -1;
          let matchLot =
            boxInfo.subProject.name.toLowerCase().indexOf(query) > -1;
          let matchProject =
            boxInfo.project.name.toLowerCase().indexOf(query) > -1;
          return matchBoxNumber || matchLot || matchProject;
        });
      }

      return boxes;
    },
    currentOpperation() {
      if (this.nfcAvailable) {
        return (this.scannedBoxNumber.length < 1 && 'Scanning') || 'Processing';
      } else {
        return 'Find Manually';
      }
    },
  },
  watch: {
    visible(visible) {
      if (visible) {
        this.setupNFC();
      } else {
        this.stopNFC();
      }
    },
    scannedBoxInfo(workOrder) {
      if (workOrder) {
        this.boxSelected(workOrder);
      } else {
        //TODO: show some modal alert about box not being found and reset state
      }
    },
  },
  apollo: {
    boxes: {
      query: DISPATCH_SETS_CURRENT,
      loadingKey: 'loadingBoxes',
      variables() {
        return { status: 'Current' };
      },
      update(data) {
        if (this.capturingScreenshots) {
          return Mock.currentDispatchSets;
        }
        return data.dispatchSets;
      },
      error(error) {
        window.console.error(error);
      },
    },
    scannedBoxInfo: {
      query: DISPATCH_SET_BY_BOX,
      loadingKey: 'loadingScanned',
      variables() {
        return { boxNumber: this.scannedBoxNumber };
      },
      skip() {
        return !this.scannedBoxNumber || this.scannedBoxNumber.length < 1;
      },
      update: data => data.dispatchSetByBoxNumber,
    },
  },
  methods: {
    setupNFC() {
      // FOR SCREENSHOTS
      if (this.capturingScreenshots) {
        this.nfcAvailable = true;
        return;
      }

      let nfc = window.nfc;

      if (nfc) {
        if (nfc.beginSession) {
          nfc.beginSession(
            () => {},
            () => {
              window.console.error('RED CW | nfc beginSession error');
            }
          );
        }

        this.nfcAvailable = true;
        nfc.addNdefListener(
          this.tagFound,
          () => {},
          reason => {
            window.console.error(reason, 'There was an nfc error');
          }
        );
      }
    },
    stopNFC() {
      let nfc = window.nfc;

      if (nfc) {
        if (nfc.invalidateSession) {
          nfc.invalidateSession(
            () => {},
            () => {
              window.console.error('RED CW | nfc invalidateSession error');
            }
          );
        }
        nfc.removeNdefListener(
          this.tagFound,
          () => {},
          reason => {
            window.console.error(reason, 'There was an nfc error');
          }
        );
      }
    },
    toggleFindManually() {
      this.userChoseFindManually = !this.userChoseFindManually;
    },
    tagFound(nfcEvent) {
      this.stopNFC();

      let nfc = window.nfc;
      let tag = nfcEvent.tag;
      let record = tag.ndefMessage && tag.ndefMessage[0];
      let payload = record && record.payload;

      let boxNumber = payload && nfc && nfc.bytesToString(payload);
      boxNumber = boxNumber && boxNumber.replace(/en/g, '');

      this.scannedBoxNumber = boxNumber;
    },
    fakeScanBox() {
      // this.scannedBoxNumber = '48506-30';
    },
    boxSelected(boxInfo) {
      this.$router.push({
        path: `/projects/${boxInfo.projectId}/box/${boxInfo.workOrderId}`,
      });
      this.$modal.hide('*');
    },
  },
  mounted() {
    this.setupNFC();
  },
  beforeDestroy() {
    this.stopNFC();
  },
};
</script>

<style lang="stylus" scoped>
.cw-scan .hero {
  background: #231F20

  .title {
    color: white;
  }
}
.scanning-icon {
  padding: 3rem
  max-width: 20rem
}
.rolloff-img {
  max-width: 30em
}
.rolloff-footer .hero-body {
  margin-bottom: -3.5rem
}
</style>
